import { Box, Center, Loader, Text, useComputedColorScheme } from '@mantine/core';
import { useEffect, useId, useState } from 'react';

/**
 * Renders a Mermaid diagram as inline SVG, themed to the current color scheme.
 *
 * Mermaid is loaded lazily so its (large) bundle stays out of the admin's main
 * chunk — only screens that actually draw diagrams pay for it. Re-renders when
 * the color scheme flips so the diagram never sits dark-on-dark.
 */
export function Mermaid({ chart }: { chart: string }) {
  const scheme = useComputedColorScheme('light', { getInitialValueInEffect: true });
  // useId() emits colons, which are not valid in the DOM ids Mermaid generates.
  const domId = `mermaid-${useId().replace(/[^a-zA-Z0-9]/g, '')}`;
  const [svg, setSvg] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;

    void (async () => {
      try {
        const { default: mermaid } = await import('mermaid');
        mermaid.initialize({
          startOnLoad: false,
          theme: scheme === 'dark' ? 'dark' : 'neutral',
          securityLevel: 'strict',
          fontFamily: 'inherit',
          sequence: { useMaxWidth: true, wrap: true },
          flowchart: { useMaxWidth: true },
        });
        const { svg: rendered } = await mermaid.render(domId, chart);
        if (!cancelled) {
          setSvg(rendered);
          setError(null);
        }
      } catch (cause) {
        if (!cancelled) {
          setError(cause instanceof Error ? cause.message : 'Diagram could not be rendered');
          setSvg(null);
        }
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [chart, scheme, domId]);

  if (error) {
    return (
      <Text c="red" size="sm">
        Diagram could not be rendered: {error}
      </Text>
    );
  }

  if (!svg) {
    return (
      <Center h={160}>
        <Loader size="sm" />
      </Center>
    );
  }

  return (
    <Box
      style={{ overflowX: 'auto' }}
      // Mermaid output is generated from the static chart strings in this repo,
      // never from user input, and Mermaid runs with securityLevel 'strict'.
      dangerouslySetInnerHTML={{ __html: svg }}
    />
  );
}
